import { useAccessibility } from "../../../Store/contexts/AccessibilityContext";
import "./styles.css";
import { ReactNode, useEffect, useRef } from "react";

interface Props {
  headers?: string[];
  data: (ReactNode | string)[][];
  columnWidths?: string[];
  rowStyleCondition?: (rowIndex: number) => boolean;
}

const DefaultTable: React.FC<Props> = ({
  headers,
  data,
  columnWidths,
  rowStyleCondition,
}) => {
  const { contrastMode } = useAccessibility();
  const tableRef = useRef<HTMLTableElement>(null);

  const handleCellFocus = (e: React.FocusEvent<HTMLTableCellElement>) => {
    e.preventDefault();

    const focusableElements = document.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

    const currentIndex = Array.from(focusableElements).indexOf(e.target);
    const nextElement =
      focusableElements[currentIndex + 1] || focusableElements[0];

    (nextElement as HTMLElement).focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Tab") {
      e.preventDefault();
      const isShiftTab = e.shiftKey;
      
      // Seleciona todos os elementos focáveis, incluindo os com tabIndex="0"
      const focusableElements = document.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
  
      const targetElement = e.target as HTMLElement;
      const currentIndex = Array.from(focusableElements).indexOf(targetElement);
  
      // Calcula o próximo índice baseado na direção do Tab ou Shift+Tab
      let nextIndex = isShiftTab ? currentIndex - 1 : currentIndex + 1;
  
      // Se o índice for menor que 0, vai para o último item
      if (nextIndex < 0) nextIndex = focusableElements.length - 1;
      // Se o índice for maior ou igual ao número de elementos, vai para o primeiro item
      if (nextIndex >= focusableElements.length) nextIndex = 0;
  
      // Obtém o próximo elemento focável
      const nextElement = focusableElements[nextIndex] as HTMLElement;
  
      // Foca no próximo elemento
      nextElement.focus();
    }
  };

  const handleKeyUp = (e: React.KeyboardEvent) => {
    if (e.key === "Tab" && e.shiftKey) {
      // Similar logic to handle the navigation for Shift + Tab
      e.preventDefault();

      const focusableElements = document.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );

      const targetElement = e.target as HTMLElement;
      const currentIndex = Array.from(focusableElements).indexOf(targetElement);

      // Calculate the previous index based on Shift + Tab
      let prevIndex = currentIndex - 1;

      if (prevIndex < 0) prevIndex = focusableElements.length - 1;

      const prevElement = focusableElements[prevIndex] as HTMLElement;
      prevElement.focus();
    }
  };

  return (
    <div
      ref={tableRef}
      className={`default-table-container ${contrastMode ? "contrast" : ""}`}
    >
      <table className="default-table">
        {headers && (
          <thead>
            <tr>
              {headers.map((header, index) => (
                <th
                  key={index}
                  style={{ width: columnWidths ? columnWidths[index] : "auto" }}
                  tabIndex={-1}
                  onFocus={handleCellFocus}
                  onKeyDown={handleKeyDown}
                  onKeyUp={handleKeyUp}
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
        )}
        <tbody>
          {!headers && <tr />}
          {data.map((row, rowIndex) => {
            const isRowStyled =
              rowStyleCondition && rowStyleCondition(rowIndex);
            return (
              <tr
                key={rowIndex}
                style={{ background: isRowStyled ? " #00995D " : "" }}
              >
                {row.map((cell, cellIndex) => (
                  <td
                    key={cellIndex}
                    className={isRowStyled ? "rowSelect" : ""}
                    style={{
                      width: columnWidths ? columnWidths[cellIndex] : "auto",
                    }}
                    tabIndex={-1}
                    onFocus={handleCellFocus}
                    onKeyDown={handleKeyDown}
                     onKeyUp={handleKeyUp}
                  >
                    {cell}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default DefaultTable;
